import { type ReactNode, useState } from 'react'
import invariant from 'tiny-invariant'
import { Facepile, type FacepileTotem, type FacepileUserState } from './Facepile'
import { MemberInterestDialog } from './MemberInterestDialog'
import type { PartialRange, UserProfile } from '../types'
import type { CheckInMark } from '../hooks/useCheckIns'
import type { TicketMarks } from '../hooks/useTicketMarksByEvent'
import { useForceLoading } from '../lib/skeletonDebug'
import { useHistoryState } from '../hooks/useHistoryState'
import { cn } from '../lib/utils'

const NO_IDS: ReadonlySet<string> = new Set()
const unique = (ids: string[]) => [...new Set(ids)]

interface Props {
  loading?: boolean
  interestedMemberIds?: string[]
  goingMemberIds?: string[]
  profiles?: Record<string, UserProfile>
  currentUserId?: string | null
  currentUserStatus?: 'interested' | 'going' | null
  totems?: FacepileTotem[]
  /** Members here for only part of the set — they get the clock badge. */
  memberRanges?: Record<string, PartialRange>
  /** Festival mode: members checked in here, or checked in at another set. */
  memberCheckIns?: Record<string, CheckInMark>
  dialogTitle?: string
  dialogSubtitle?: string
  /** People going to a pre-game the viewer can see — marked in the dialog's list. */
  pregameGoerIds?: ReadonlySet<string>
  /** Who holds or was given a ticket: holders count as going, unconfirmed ones are listed as tentative — RSVP or not. */
  tickets?: TicketMarks
  /** Unique on the page — keeps the dialog open across a profile visit + swipe back. */
  historyKey?: string
  size?: 'sm' | 'md'
  max?: number
  responsive?: boolean
  trigger?: (open: () => void) => ReactNode
  onClick?: () => void
}

export function MemberFacepile({
  loading: loadingProp,
  interestedMemberIds,
  goingMemberIds,
  profiles,
  currentUserId,
  currentUserStatus,
  totems,
  memberRanges,
  memberCheckIns,
  dialogTitle,
  dialogSubtitle,
  pregameGoerIds,
  tickets,
  historyKey,
  size = 'sm',
  max = 5,
  responsive = false,
  trigger,
  onClick,
}: Props) {
  const [open, setOpen] = useHistoryState(historyKey ? `facepile:${historyKey}` : null, false)
  const loading = useForceLoading(loadingProp)
  // Only animate the facepile in if the user actually watched it load.
  const [wasLoading, setWasLoading] = useState(false)
  if (loading && !wasLoading) setWasLoading(true)

  if (loading) return null

  invariant(interestedMemberIds, 'MemberFacepile: interestedMemberIds is required when not loading')
  invariant(goingMemberIds, 'MemberFacepile: goingMemberIds is required when not loading')
  invariant(profiles, 'MemberFacepile: profiles is required when not loading')

  // Same rules as the event page's people list: a ticket in hand means going,
  // RSVP or not, and an unconfirmed one lists them as tentative.
  const holders = tickets?.holderIds ?? NO_IDS
  const goingIds = unique([...goingMemberIds, ...holders])
  const goingSet = new Set(goingIds)
  const interestedIds = interestedMemberIds.filter(uid => !goingSet.has(uid))
  const tentativeIds = [...(tickets?.pendingIds ?? NO_IDS)].filter(uid => !goingSet.has(uid))

  if (interestedIds.length === 0 && goingIds.length === 0 && tentativeIds.length === 0 && (!totems || totems.length === 0)) return null

  let userStates: Record<string, FacepileUserState> | undefined = memberRanges
    ? Object.fromEntries(Object.keys(memberRanges).map(uid => [uid, { partial: true }]))
    : undefined
  if (memberCheckIns) {
    userStates = { ...userStates }
    for (const [uid, mark] of Object.entries(memberCheckIns)) {
      userStates[uid] = { ...userStates[uid], ...(mark === 'here' ? { checkedIn: true } : { dimmed: true }) }
    }
  }
  const userIds = unique([...goingIds, ...tentativeIds, ...interestedIds])

  const dialogInterestedIds = currentUserId && currentUserStatus === 'interested' && !goingSet.has(currentUserId)
    ? [currentUserId, ...interestedIds]
    : interestedIds
  const dialogGoingIds = currentUserId && currentUserStatus === 'going'
    ? unique([currentUserId, ...goingIds])
    : goingIds

  const openDialog = onClick ?? (() => setOpen(true))

  return (
    <>
      {trigger ? (
        trigger(openDialog)
      ) : (
        <button
          type="button"
          className={cn('cursor-pointer', responsive && 'flex-1 min-w-0')}
          data-testid="member-facepile"
          onClick={e => {
            e.stopPropagation()
            openDialog()
          }}
        >
          <Facepile
            userIds={userIds}
            goingUserIds={goingSet}
            userStates={userStates}
            profiles={profiles}
            totems={totems}
            size={size}
            max={max}
            responsive={responsive}
            animate={wasLoading}
          />
        </button>
      )}
      {!onClick && (
        <MemberInterestDialog
          open={open}
          onOpenChange={setOpen}
          interestedMemberIds={dialogInterestedIds}
          goingMemberIds={dialogGoingIds}
          tentativeIds={tentativeIds}
          profiles={profiles}
          currentUserId={currentUserId}
          name={dialogTitle}
          subtitle={dialogSubtitle}
          pregameGoerIds={pregameGoerIds}
          tickets={tickets}
        />
      )}
    </>
  )
}
